import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { getNestedValue } from './utils';

/**
 * Color palette matching the dark theme
 */
const CHART_COLORS = {
  red: '#ef4444',
  orange: '#f59e0b',
  emerald: '#10b981',
  indigo: '#7367F0',
  slate: '#94a3b8',
  blue: '#3b82f6',
  purple: '#a855f7',
  pink: '#ec4899',
  cyan: '#06b6d4',
  yellow: '#eab308',
};

const DEFAULT_PALETTE = [
  CHART_COLORS.indigo,
  CHART_COLORS.emerald,
  CHART_COLORS.orange,
  CHART_COLORS.red,
  CHART_COLORS.blue,
  CHART_COLORS.purple,
  CHART_COLORS.cyan,
  CHART_COLORS.pink,
];

const resolveReferenceSourceValue = (data, sourceKey, dataKey) => {
  if (!sourceKey) return null;

  const sourceValue = getNestedValue(data, sourceKey, undefined);
  if (sourceValue === undefined || sourceValue === null) return null;

  const normalizedDataKey = typeof dataKey === 'string' && dataKey.startsWith(`${sourceKey}.`)
    ? dataKey.slice(`${sourceKey}.`.length)
    : dataKey;

  if (normalizedDataKey) {
    if (Array.isArray(sourceValue)) {
      const matched = sourceValue.find((row) => String(row?.key) === String(normalizedDataKey));
      if (matched && Object.prototype.hasOwnProperty.call(matched, 'value')) {
        return matched.value;
      }
    }

    if (sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
      const nestedValue = getNestedValue(sourceValue, normalizedDataKey, undefined);
      if (nestedValue !== undefined) {
        return nestedValue;
      }
    }

    const mergedPath = dataKey && dataKey.startsWith(`${sourceKey}.`)
      ? dataKey
      : `${sourceKey}.${normalizedDataKey}`;
    const nestedFromRoot = getNestedValue(data, mergedPath, undefined);
    if (nestedFromRoot !== undefined) {
      return nestedFromRoot;
    }
  }

  return sourceValue;
};

const parseNumericOption = (value) => {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const buildLineAxisRange = (seriesValues, referenceLines = [], data = {}, yMinOption, yMaxOption) => {
  const explicitMin = parseNumericOption(yMinOption);
  const explicitMax = parseNumericOption(yMaxOption);
  if (explicitMin !== undefined || explicitMax !== undefined) {
    return {
      min: explicitMin,
      max: explicitMax,
    };
  }

  const numericSeries = seriesValues.filter((value) => Number.isFinite(value));
  if (numericSeries.length === 0) {
    return { min: undefined, max: undefined };
  }

  const baseMin = Math.min(...numericSeries);
  const baseMax = Math.max(...numericSeries);
  let nextMin = baseMin;
  let nextMax = baseMax;

  referenceLines.forEach((ref) => {
    if (!ref) return;
    if (ref.type === 'average') return;

    let resolvedValue = null;
    if (ref.type === 'source') {
      resolvedValue = resolveReferenceSourceValue(data, ref.source_key, ref.dataKey);
    } else {
      resolvedValue = ref.value;
    }

    const numericRef = Number(resolvedValue);
    if (!Number.isFinite(numericRef)) return;

    if (numericRef < nextMin) nextMin = numericRef;
    if (numericRef > nextMax) nextMax = numericRef;
  });

  if (nextMin === baseMin && nextMax === baseMax) {
    return { min: undefined, max: undefined };
  }

  return {
    min: nextMin,
    max: nextMax,
  };
};

/**
 * Resolve chart data from items + enrichedData
 * Each item's value comes from source_key (mapped to dataKey by SectionRenderer) or direct dataKey
 * 
 * Supports two modes:
 * 1. Scalar: data[key] is a number → single data point (pie, bar, gauge, radar)
 * 2. Array:  data[key] is an array of {label, value, ...} → array-based charts
 *    (line/bar time-series or pie/donut slices)
 */
const resolveChartData = (items, data) => {
  if (!items || !Array.isArray(items)) return [];
  return items.map((item, idx) => {
    let raw = getNestedValue(data, item.dataKey, 0);

    if (!Array.isArray(raw) && item.source_key) {
      const sourceRows = getNestedValue(data, item.source_key, null);
      if (Array.isArray(sourceRows)) {
        const prefix = `${item.source_key}.`;
        const seriesKey = typeof item.dataKey === 'string' && item.dataKey.startsWith(prefix)
          ? item.dataKey.slice(prefix.length)
          : item.dataKey;

        if (seriesKey) {
          const filteredRows = sourceRows.filter((row) => String(row?.key) === String(seriesKey));
          if (filteredRows.length > 0) {
            raw = filteredRows;
          }
        }
      }
    }

    const color = CHART_COLORS[item.color] || DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length];
    
    // Array data (line/bar series or pie/donut slices)
    if (Array.isArray(raw)) {
      const labelKey = item.labelKey || 'label';
      const valueKey = item.valueKey || 'value';
      return {
        name: item.label || `Series ${idx + 1}`,
        seriesData: raw.map(row => ({
          label: row[labelKey] || '',
          value: Number(row[valueKey]) || 0,
        })),
        color,
        isArray: true,
      };
    }
    
    // Scalar data (single value)
    return {
      name: item.label || `Item ${idx + 1}`,
      value: Number(raw) || 0,
      color,
      max: item.max || undefined,
      isArray: false,
    };
  });
};

/**
 * Build markLine config from referenceLines
 * @param {Array} referenceLines - Reference line definitions
 * @param {boolean} isHorizontal - Whether chart is horizontal (affects axis key)
 * @param {Object} data - Resolved data for dynamic source lookups
 */
const buildMarkLine = (referenceLines, isHorizontal = false, data = {}) => {
  if (!referenceLines?.length) return undefined;
  return {
    silent: true,
    symbol: 'none',
    label: { fontSize: 9, fontWeight: 700, fontFamily: 'Pretendard, sans-serif' },
    data: referenceLines.map(ref => {
      const lineColor = CHART_COLORS[ref.color] || ref.color || '#94a3b8';
      if (ref.type === 'average') {
        return {
          type: 'average',
          name: ref.label || '평균',
          lineStyle: { color: lineColor, type: ref.lineStyle || 'dashed', width: 1 },
          label: { formatter: ref.label || '평균', color: lineColor, position: 'insideEndTop' },
        };
      }
      
      // Resolve value: fixed or from data source
      let resolvedValue = ref.value;
      if (ref.type === 'source' && ref.source_key) {
        const sourceData = resolveReferenceSourceValue(data, ref.source_key, ref.dataKey);
        if (sourceData !== undefined && sourceData !== null) {
          resolvedValue = Number(sourceData) || 0;
        }
      }
      
      const axisKey = isHorizontal ? 'xAxis' : 'yAxis';
      return {
        [axisKey]: resolvedValue,
        name: ref.label || '',
        lineStyle: { color: lineColor, type: ref.lineStyle || 'dashed', width: 1 },
        label: { formatter: `${ref.label || ''}: ${resolvedValue}`, color: lineColor, position: 'insideEndTop' },
      };
    }).filter(d => d.type === 'average' || d.xAxis !== undefined || d.yAxis !== undefined),
  };
};

/**
 * Build ECharts option based on chart type
 */
const buildChartOption = (chartType, chartData, options, data = {}) => {
  const baseTextStyle = {
    color: 'rgba(148,163,184,0.8)',
    fontFamily: 'Pretendard, sans-serif',
    fontWeight: 600,
  };

  const names = chartData.map(d => d.name);
  const values = chartData.map(d => d.value);
  const colors = chartData.map(d => d.color);

  switch (chartType) {
    case 'pie':
    case 'donut': {
      let paletteIndex = 0;
      const pieData = chartData.flatMap((series, seriesIdx) => {
        if (series.isArray && Array.isArray(series.seriesData)) {
          return series.seriesData.map((point, pointIdx) => ({
            value: Number(point.value) || 0,
            name: point.label || `${series.name} ${pointIdx + 1}`,
            itemStyle: { color: DEFAULT_PALETTE[paletteIndex++ % DEFAULT_PALETTE.length] },
          }));
        }

        return [{
          value: Number(series.value) || 0,
          name: series.name,
          itemStyle: { color: series.color || DEFAULT_PALETTE[seriesIdx % DEFAULT_PALETTE.length] },
        }];
      });

      const pieColors = pieData
        .map((slice) => slice.itemStyle?.color)
        .filter(Boolean);

      return {
        tooltip: {
          trigger: 'item',
          appendToBody: true,
          backgroundColor: 'rgba(15,23,42,0.95)',
          borderColor: 'rgba(255,255,255,0.1)',
          textStyle: { color: '#e2e8f0', fontSize: 11 },
          formatter: '{b}: {c} ({d}%)',
        },
        legend: {
          bottom: 0,
          textStyle: { ...baseTextStyle, fontSize: 10 },
          itemWidth: 8,
          itemHeight: 8,
          itemGap: 8,
        },
        color: pieColors.length > 0 ? pieColors : colors,
        series: [{
          type: 'pie',
          radius: chartType === 'donut' ? ['45%', '70%'] : ['0%', '70%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          label: {
            show: pieData.length <= 4,
            formatter: '{d}%',
            color: '#94a3b8',
            fontSize: 10,
            fontWeight: 700,
          },
          labelLine: { show: pieData.length <= 4, lineStyle: { color: 'rgba(148,163,184,0.3)' } },
          emphasis: {
            itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.5)' },
          },
          data: pieData,
        }],
      };
    }

    case 'bar':
    case 'horizontal_bar':
      const isHorizontal = chartType === 'horizontal_bar';
      const categoryAxis = {
        type: 'category',
        data: names,
        axisLabel: { ...baseTextStyle, fontSize: 10 },
        axisLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
        axisTick: { show: false },
      };
      const valueAxis = {
        type: 'value',
        axisLabel: { ...baseTextStyle, fontSize: 10 },
        splitLine: { lineStyle: { color: 'rgba(255,255,255,0.04)' } },
        axisLine: { show: false },
        axisTick: { show: false },
      };

      const barSeries = [{
        type: 'bar',
        data: values.map((v, i) => ({
          value: v,
          itemStyle: { color: colors[i], borderRadius: isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0] },
        })),
        barWidth: options.barWidth || '60%',
        emphasis: { itemStyle: { brightness: 0.2 } },
      }];

      // Add reference lines if provided
      if (options.referenceLines?.length > 0) {
        barSeries[0].markLine = buildMarkLine(options.referenceLines, isHorizontal, data);
      }

      return {
        tooltip: {
          trigger: 'axis',
          appendToBody: true,
          backgroundColor: 'rgba(15,23,42,0.95)',
          borderColor: 'rgba(255,255,255,0.1)',
          textStyle: { color: '#e2e8f0', fontSize: 11 },
        },
        grid: {
          left: isHorizontal ? 60 : 30,
          right: 10,
          top: 10,
          bottom: 20,
          containLabel: false,
        },
        xAxis: isHorizontal ? valueAxis : categoryAxis,
        yAxis: isHorizontal ? categoryAxis : valueAxis,
        series: barSeries,
      };

    case 'line': {
      // Check if any item has array (time-series) data
      const hasArrayData = chartData.some(d => d.isArray);
      
      if (hasArrayData) {
        // === TIME-SERIES MODE: multiple data points per series ===
        // Use first series' labels as x-axis categories
        const firstArrayItem = chartData.find(d => d.isArray);
        const xLabels = firstArrayItem ? firstArrayItem.seriesData.map(d => d.label) : [];
        
        const lineSeries = chartData
          .filter(d => d.isArray && d.seriesData.length > 0)
          .map((d, idx) => {
            const seriesColor = d.color || DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length];
            const series = {
              type: 'line',
              name: d.name,
              data: d.seriesData.map(p => p.value),
              smooth: true,
              symbol: 'circle',
              symbolSize: 4,
              lineStyle: { width: 2, color: seriesColor },
              itemStyle: { color: seriesColor },
            };
            // Only first series gets area fill
            if (idx === 0) {
              series.areaStyle = {
                color: {
                  type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [
                    { offset: 0, color: seriesColor + '30' },
                    { offset: 1, color: seriesColor + '05' },
                  ],
                },
              };
            }
            return series;
          });

        const lineSeriesValues = lineSeries.flatMap((series) => series.data || []);
        const lineAxisRange = buildLineAxisRange(
          lineSeriesValues,
          options.referenceLines,
          data,
          options.yMin,
          options.yMax
        );
        
        // Add reference lines to first series
        if (options.referenceLines?.length > 0 && lineSeries.length > 0) {
          lineSeries[0].markLine = buildMarkLine(options.referenceLines, false, data);
        }
        
        return {
          tooltip: {
            trigger: 'axis',
            appendToBody: true,
            backgroundColor: 'rgba(15,23,42,0.95)',
            borderColor: 'rgba(255,255,255,0.1)',
            textStyle: { color: '#e2e8f0', fontSize: 11 },
          },
          legend: lineSeries.length > 1 ? {
            bottom: 0,
            textStyle: { ...baseTextStyle, fontSize: 10 },
            itemWidth: 12, itemHeight: 3, itemGap: 10,
          } : undefined,
          grid: { left: 35, right: 10, top: 10, bottom: lineSeries.length > 1 ? 30 : 20, containLabel: false },
          xAxis: {
            type: 'category',
            data: xLabels,
            boundaryGap: false,
            axisLabel: {
              ...baseTextStyle,
              fontSize: 9,
              rotate: xLabels.length > 3 ? 15 : 0,
              showMinLabel: true,
              showMaxLabel: true,
              formatter: (value, index) => {
                const [date, time] = value.split(' ');

                if (index === 0) return date;

                const [prevDate] = xLabels[index - 1].split(' ');
                return date !== prevDate ? date : time;
              },
            },
            axisLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
            axisTick: { show: false },
          },
          yAxis: {
            type: 'value',
            scale: true,
            min: lineAxisRange.min,
            max: lineAxisRange.max,
            axisLabel: { ...baseTextStyle, fontSize: 10 },
            splitLine: { lineStyle: { color: 'rgba(255,255,255,0.04)' } },
            axisLine: { show: false },
            axisTick: { show: false },
          },
          series: lineSeries,
        };
      }
      
      // === SCALAR MODE: one value per item (legacy) ===
      const lineSeries = [{
        type: 'line',
        data: values,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2, color: colors[0] || DEFAULT_PALETTE[0] },
        itemStyle: { color: colors[0] || DEFAULT_PALETTE[0] },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: (colors[0] || DEFAULT_PALETTE[0]) + '40' },
              { offset: 1, color: (colors[0] || DEFAULT_PALETTE[0]) + '05' },
            ],
          },
        },
      }];

      // Add reference lines if provided
      if (options.referenceLines?.length > 0) {
        lineSeries[0].markLine = buildMarkLine(options.referenceLines, false, data);
      }

      const scalarLineAxisRange = buildLineAxisRange(
        values,
        options.referenceLines,
        data,
        options.yMin,
        options.yMax
      );

      return {
        tooltip: {
          trigger: 'axis',
          appendToBody: true,
          backgroundColor: 'rgba(15,23,42,0.95)',
          borderColor: 'rgba(255,255,255,0.1)',
          textStyle: { color: '#e2e8f0', fontSize: 11 },
        },
        grid: { left: 30, right: 10, top: 10, bottom: 20, containLabel: false },
        xAxis: {
          type: 'category',
          data: names,
          axisLabel: { ...baseTextStyle, fontSize: 10 },
          axisLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
          axisTick: { show: false },
          boundaryGap: false,
        },
        yAxis: {
          type: 'value',
          scale: true,
          min: scalarLineAxisRange.min,
          max: scalarLineAxisRange.max,
          axisLabel: { ...baseTextStyle, fontSize: 10 },
          splitLine: { lineStyle: { color: 'rgba(255,255,255,0.04)' } },
          axisLine: { show: false },
          axisTick: { show: false },
        },
        series: lineSeries,
      };
    }

    case 'gauge':
      const gaugeValue = values[0] || 0;
      const gaugeColor = colors[0] || DEFAULT_PALETTE[0];
      return {
        series: [{
          type: 'gauge',
          center: ['50%', '60%'],
          radius: '90%',
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: options.max || 100,
          progress: {
            show: true,
            width: 12,
            itemStyle: { color: gaugeColor },
          },
          pointer: { show: false },
          axisLine: {
            lineStyle: { width: 12, color: [[1, 'rgba(255,255,255,0.05)']] },
          },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          detail: {
            valueAnimation: true,
            formatter: `{value}${options.unit || '%'}`,
            color: gaugeColor,
            fontSize: 22,
            fontWeight: 900,
            fontFamily: 'Pretendard, sans-serif',
            offsetCenter: [0, '10%'],
          },
          title: {
            show: !!names[0],
            text: names[0] || '',
            offsetCenter: [0, '35%'],
            color: '#94a3b8',
            fontSize: 11,
            fontWeight: 600,
          },
          data: [{ value: gaugeValue, name: names[0] || '' }],
        }],
      };

    case 'radar': {
      const indicators = chartData.map(d => ({ name: d.name, max: d.max || 100 }));
      const radarValues = chartData.map(d => d.value);

      return {
        radar: {
          indicator: indicators,
          shape: options.radarShape || 'polygon',
          radius: '65%',
          axisName: { 
            color: 'rgba(148,163,184,0.8)', 
            fontSize: 10, 
            fontWeight: 600,
          },
          splitArea: { areaStyle: { color: ['rgba(255,255,255,0.02)', 'rgba(255,255,255,0.04)'] } },
          splitLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
          axisLine: { lineStyle: { color: 'rgba(255,255,255,0.08)' } },
        },
        tooltip: { 
          trigger: 'item', 
          appendToBody: true, 
          backgroundColor: 'rgba(15,23,42,0.95)', 
          borderColor: 'rgba(255,255,255,0.1)', 
          textStyle: { color: '#e2e8f0', fontSize: 11 },
          formatter: (params) => {
            const values = params.value;
            return params.name + '<br/>' +
              indicators.map((ind, i) =>
                `${ind.name}: ${values[i]} ${options.unit || ''}`
              ).join('<br/>');
          }
        },
        series: [{
          type: 'radar',
          symbol: 'circle',
          symbolSize: 5,
          data: [{
            value: radarValues,
            name: options.title || '',
            areaStyle: options.radarFill !== false ? { opacity: 0.15, color: colors[0] || DEFAULT_PALETTE[0] } : undefined,
            lineStyle: { width: 2, color: colors[0] || DEFAULT_PALETTE[0] },
            itemStyle: { color: colors[0] || DEFAULT_PALETTE[0] },
          }],
        }],
      };
    }

    default:
      return {};
  }
};

/**
 * ChartWidget - Generic chart component powered by ECharts
 * 
 * Config (from config_json):
 * {
 *   "type": "chart",
 *   "options": {
 *     "chartType": "pie",       // pie | donut | bar | horizontal_bar | line | gauge
 *     "height": 200,            // Chart height in pixels
 *     "title": "위험 현황",      // Optional title above chart
 *     "max": 100,               // For gauge: max value
 *     "unit": "%",              // For gauge: unit suffix
 *     "barWidth": "60%"         // For bar: bar width
 *   },
 *   "items": [
 *     { "source_key": "risk_count_high", "label": "고위험", "color": "red" },
 *     { "source_key": "risk_count_mid", "label": "중위험", "color": "orange" },
 *     { "source_key": "risk_count_low", "label": "저위험", "color": "emerald" }
 *   ]
 * }
 */
export default function ChartWidget({ config, data }) {
  const chartType = config?.chartType || 'bar';
  const height = config?.height || 200;
  const chartData = useMemo(() => {
    const items = config?.items || [];
    return resolveChartData(items, data);
  }, [config?.items, data]);
  const option = useMemo(() => buildChartOption(chartType, chartData, config, data), [chartType, chartData, config, data]);

  if (!config) return null;

  // Don't render if all values are 0 or all series are empty
  const hasData = chartData.some(d => 
    d.isArray ? (d.seriesData && d.seriesData.length > 0) : d.value > 0
  );

  return (
    <div>
      {config.title && (
        <div className="flex items-center justify-between px-1 mb-2">
          <span className="text-[12px] text-slate-400 font-bold">{config.title}</span>
        </div>
      )}
      {hasData ? (
        <ReactECharts
          option={option}
          style={{ height: `${height}px`, width: '100%' }}
          opts={{ renderer: 'canvas' }}
          theme="dark"
          notMerge={true}
        />
      ) : (
        <div
          className="flex items-center justify-center rounded-2xl bg-white/5 text-slate-600 text-[11px] font-bold"
          style={{ height: `${height}px`, border: '1px solid rgba(255,255,255,0.04)' }}
        >
          데이터 없음
        </div>
      )}
    </div>
  );
}
